/**
 * scheduleTasks.ts
 * Frontend service for the Task Table UI — Phase 2, Item 3.
 *
 * All API calls go through apiClient (MSAL bearer + base URL); no direct fetch from components.
 *
 * Endpoints consumed:
 *   GET    /api/schedule/tasks?source=...&sourceKey=...
 *   PUT    /api/schedule/tasks/:key
 *   DELETE /api/schedule/tasks/:key
 *   PUT    /api/schedule/bookings/:key/machine
 *   GET    /api/schedule/task-types/:key/capable-machines
 */

import apiClient from './apiClient';
import { deleteEmployeeAssignment } from './scheduleAssignments';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BookingView {
  MachineBookingKey: string;
  MachineKey: string;
  MachineCode: string | null;
  MachineName: string | null;
  StartDate: string;
  EndDate: string;
  Status: 'Tentative' | 'Committed';
  Source: 'Quote' | 'WorkOrder' | 'Manual';
  ScenarioKey: string;
  /**
   * Per-machine quantity (080): hand-tuned tests on this machine in a split task,
   * or null → the row shows the even-split share. Drives this booking's duration.
   */
  SplitQty: number | null;
}

export interface TaskWithBookings {
  ScheduleTaskKey: string;
  Name: string;
  TaskTypeKey: string | null;
  TaskTypeName: string | null;
  TaskTypeCategory: string | null;
  TaskTypeLabSection: string | null;
  SourceType: 'Quote' | 'WorkOrder' | 'Manual';
  QuoteKey: string | null;
  WorkOrderKey: string | null;
  /**
   * Prep consolidation (BUG-2026-0165, 2026-07-26): the source LINE this task
   * belongs to. A prep row carries its parent TEST's line key, so the UI can
   * resolve which test a Cutting/Grinding/Conformity row preps by finding the
   * non-prep sibling with the same key (see scenario/prepRollup.ts). Optional —
   * older mocks/partials still type-check; the live API always sends them.
   */
  QuoteLineItemKey?: string | null;
  WorkOrderDetailKey?: string | null;
  /**
   * Work-order / project / quote identity for the booking-bar labels (2026-06-24). The
   * Phase-2 bars lead with the WORK (WorkOrderName ?? ProjectName, "(WO-id)") like the
   * Machine Schedule; the test name stays as the row label. Null when not applicable.
   * Optional so older mocks/partials still type-check — the live API always sends them.
   */
  WorkOrderNo?: string | null;
  WorkOrderName?: string | null;
  ProjectName?: string | null;
  QuoteNumber?: string | null;
  PresetDurationDays: number;
  MachineCount: number;
  /**
   * Phase 6.5 / REQ-05 / D1: resolved machine time in minutes for this task.
   * Populated during task generation (item 6.2) from the MachineTaskCapability
   * row for the chosen machine. Null when no capable machine is mapped yet.
   * Displayed as "{n} min" in the Scenario Planning Phase-1 task table.
   */
  MachineMinutes: number | null;
  Notes: string | null;
  /** Earliest StartDate across active bookings */
  StartDate: string | null;
  /** Latest EndDate across active bookings */
  EndDate: string | null;
  /**
   * FEAT-2026-0007 6B.4 — Test Qty from the source line item (QuoteLineItem.Quantity
   * or WorkOrderDetail.Quantity). Null for manual tasks with no source line item.
   */
  TestQty: number | null;
  /**
   * Phase 7.4 / REQ-06 — true when this task was auto-inserted as a hidden
   * prep step (Cutting / Grinding / Conformity).  These are schedule-only
   * artifacts: $0, no QuoteLineItem, excluded from quote totals + proposal .docx.
   * Surfaced here so the schedule UI can add a "Prep" badge to distinguish them.
   */
  IsPrepStep: boolean;
  /**
   * Run Mode Phase C — the task's RunMode snapshot ('attended' | 'continuous').
   * Continuous (24/7) rows label durations in CALENDAR days, show a 24/7 chip,
   * and render weekend run-time as running. Prep rows are always 'attended'.
   * Optional so older mocks/partials still type-check — the live API always
   * sends it.
   */
  RunMode?: 'attended' | 'continuous';
  Bookings: BookingView[];
  sysCreateUser: string;
  sysCreateDate: string;
  sysLastModifiedUser: string;
  sysLastModifiedDate: string;
  sysLastAction: string;
}

export interface UpdateTaskPayload {
  PresetDurationDays?: number;
  MachineCount?: number;
  Notes?: string | null;
  /**
   * The scenario being edited — scopes the backend's per-scenario MachineCount
   * reconcile so changing Machine Qty in a duplicated plan splits THAT plan's
   * bookings (not the original's). Omit for the non-scenario task table (2026-06-23).
   */
  ScenarioKey?: string;
}

export interface CapableMachine {
  MachineKey: string;
  Code: string;
  Name: string;
  LabSection: string | null;
  /** Migration 120: second-level machine grouping (test family). */
  MachineGroup?: string | null;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * GET /api/schedule/tasks?source=Quote|WorkOrder&sourceKey=UUID[&scenarioKey=S]
 *
 * FEAT-2026-0007 6B.4: scenarioKey is optional. When provided, only bookings
 * visible under that scenario overlay (Committed + Tentative for that draft)
 * are returned. Defaults to the live-view rule (Committed + BASELINE-tentative).
 */
export async function listTasksForSource(
  source: 'Quote' | 'WorkOrder',
  sourceKey: string,
  scenarioKey?: string,
): Promise<TaskWithBookings[]> {
  try {
    const qs = new URLSearchParams({
      source,
      sourceKey,
    });
    if (scenarioKey) qs.set('scenarioKey', scenarioKey);
    const response = await apiClient.get<TaskWithBookings[]>(
      `/schedule/tasks?${qs.toString()}`,
    );
    return response.data;
  } catch (err: any) {
    const msg = err?.response?.data?.error
      ?? `Failed to load tasks: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

/**
 * PUT /api/schedule/tasks/:key
 */
export async function updateTask(
  taskKey: string,
  payload: UpdateTaskPayload,
): Promise<TaskWithBookings> {
  try {
    const response = await apiClient.put<TaskWithBookings>(
      `/schedule/tasks/${taskKey}`,
      payload,
    );
    return response.data;
  } catch (err: any) {
    const msg = err?.response?.data?.error
      ?? `Failed to update task: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

/**
 * DELETE /api/schedule/tasks/:key
 */
export async function deleteTask(taskKey: string): Promise<void> {
  try {
    await apiClient.delete(`/schedule/tasks/${taskKey}`);
  } catch (err: any) {
    const msg = err?.response?.data?.error
      ?? `Failed to delete task: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

/**
 * PUT /api/schedule/bookings/:key/machine
 */
export async function changeBookingMachine(
  bookingKey: string,
  machineKey: string,
): Promise<void> {
  try {
    await apiClient.put(
      `/schedule/bookings/${bookingKey}/machine`,
      { MachineKey: machineKey },
    );
  } catch (err: any) {
    const msg = err?.response?.data?.error
      ?? `Failed to change booking machine: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

/**
 * Outcome of a setBookingStart call. The `needsConfirm` variant carries WHY the
 * date is contested (overlap and/or weekend/holiday) plus the earliest free
 * working-day start, so the row can offer "book anyway / use earliest free".
 */
export type SetBookingStartResult =
  | { kind: 'updated'; MachineBookingKey: string; StartDate: string; EndDate: string }
  | { kind: 'needsConfirm'; conflict: boolean; weekend: boolean; earliestFree: string; conflictLabel?: string | null };

/**
 * PUT /api/schedule/bookings/:key/start
 * Body: { StartDate: 'YYYY-MM-DD', Force?: boolean }
 *
 * Task Schedule editable Start column (BUG-2026-0073). EndDate is auto-set
 * by the backend from the parent task's per-machine span.
 *
 * Default call evaluates the warn-and-confirm gate (overlap + weekend/holiday)
 * and returns `needsConfirm` on a soft barrier. Pass `{ force: true }` after the
 * user confirms "book anyway" to persist regardless.
 */
export async function setBookingStartDate(
  bookingKey: string,
  startDate: string,
  opts?: { force?: boolean },
): Promise<SetBookingStartResult> {
  try {
    const response = await apiClient.put<{
      MachineBookingKey: string;
      StartDate: string;
      EndDate: string;
    }>(`/schedule/bookings/${bookingKey}/start`, {
      StartDate: startDate,
      ...(opts?.force ? { Force: true } : {}),
    });
    return { kind: 'updated', ...response.data };
  } catch (err: any) {
    if (err?.response?.status === 409 && err?.response?.data?.needsConfirm) {
      const d = err.response.data;
      return {
        kind: 'needsConfirm',
        conflict: Boolean(d.conflict),
        weekend: Boolean(d.weekend),
        earliestFree: d.earliestFree as string,
        conflictLabel: (d.conflictLabel as string | null | undefined) ?? null,
      };
    }
    const msg = err?.response?.data?.error
      ?? `Failed to set booking start date: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

export type SetBookingQtyResult =
  | { kind: 'updated'; MachineBookingKey: string; StartDate: string; EndDate: string; SplitQty: number }
  | { kind: 'needsConfirm'; conflictLabel?: string | null };

/**
 * PUT /api/schedule/bookings/:key/qty  (per-machine quantity — 080)
 * Body: { qty: number, Force?: boolean }
 *
 * Sets this machine's per-test quantity in a split task; the backend recomputes
 * EndDate from qty × per-test minutes and re-spans the booking's staffing.
 * Returns `needsConfirm` when the longer span overlaps another booking on the
 * machine; pass `{ force: true }` after the user confirms "save anyway".
 */
export async function setBookingQty(
  bookingKey: string,
  qty: number,
  opts?: { force?: boolean },
): Promise<SetBookingQtyResult> {
  try {
    const response = await apiClient.put<{
      MachineBookingKey: string;
      StartDate: string;
      EndDate: string;
      SplitQty: number;
    }>(`/schedule/bookings/${bookingKey}/qty`, {
      qty,
      ...(opts?.force ? { Force: true } : {}),
    });
    return { kind: 'updated', ...response.data };
  } catch (err: any) {
    if (err?.response?.status === 409 && err?.response?.data?.needsConfirm) {
      return {
        kind: 'needsConfirm',
        conflictLabel: (err.response.data.conflictLabel as string | null | undefined) ?? null,
      };
    }
    const msg = err?.response?.data?.error
      ?? `Failed to set machine quantity: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

/** Existing employee assignee on a task — for the Assign Employees dialog. */
export interface TaskAssignee {
  EmployeeAssignmentKey: string;
  UserKey: string;
  /**
   * Per-machine staffing (079): the MachineBooking this assignee staffs, or null
   * for a legacy task-level row. Drives the Phase-2 weekly grid's per-machine
   * grouping. Absent on endpoints predating 079 → treat as null.
   */
  MachineBookingKey?: string | null;
  displayName: string;
  email: string;
  tier: string | null;
  /** AppUser.PhotoUrl — Graph/stored avatar URL, null when none. Drives the Board avatars. */
  photoUrl?: string | null;
  WeekStart: string;
  StartDate: string;
  EndDate: string;
  Percent: number;
  Status: 'Tentative' | 'Committed';
  Source: 'Quote' | 'WorkOrder' | 'Manual';
}

/**
 * GET /api/schedule/tasks/:scheduleTaskKey/assignees
 * Returns currently-assigned employees for a task. Used by Assign Employees
 * dialog to show "Currently assigned" before the user picks more.
 *
 * When `scenarioKey` is supplied the backend applies the committed+draft
 * overlay, so the Phase-2 chips/weekly grid match the capacity table (one
 * scenario-scoped set). Omit it for the non-scenario Machine-Schedule task
 * table, which shows every active assignee regardless of scenario.
 */
export async function listTaskAssignees(
  scheduleTaskKey: string,
  scenarioKey?: string,
): Promise<TaskAssignee[]> {
  try {
    const r = await apiClient.get<TaskAssignee[]>(
      `/schedule/tasks/${scheduleTaskKey}/assignees`,
      scenarioKey ? { params: { scenarioKey } } : undefined,
    );
    return r.data;
  } catch (err: any) {
    const msg = err?.response?.data?.error
      ?? `Failed to load task assignees: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

/**
 * BUG-2026-0093 — unassign a single employee from a task entirely.
 *
 * A user assigned across an N-week task has N separate EmployeeAssignment rows
 * (one per WeekStart) — `listTaskAssignees` returns one row per (user, week),
 * which both the inline picker and the dialog dedupe by UserKey for display.
 * Removing the person therefore means soft-deleting EVERY matching row, not
 * just the one that survived dedup.
 *
 * This orchestration lives in the service layer (not the components) so the
 * inline chip X and the dialog chip X share one correct multi-row delete.
 *
 * @param assignees  The task's full (un-deduped) assignee list from listTaskAssignees.
 * @param userKey    UUID of the AppUser to remove from the task.
 * @returns          Number of EmployeeAssignment rows soft-deleted.
 */
export async function unassignUserFromTask(
  assignees: TaskAssignee[],
  userKey: string,
): Promise<number> {
  const keys = assignees
    .filter((a) => a.UserKey === userKey)
    .map((a) => a.EmployeeAssignmentKey);
  // Delete every per-week row. Sequential keeps the failure point obvious and
  // avoids hammering the API with a burst of parallel DELETEs for long tasks.
  let deleted = 0;
  for (const key of keys) {
    try {
      await deleteEmployeeAssignment(key);
      deleted += 1;
    } catch (err: unknown) {
      // Idempotent delete: 404 means the row is ALREADY gone — e.g. the
      // roster DELETE just swept this week's presence rows server-side
      // (removeRosterAllocation), and our assignee list predates that. The
      // goal state is reached, so continue; anything else is a real failure
      // (locked scenario, permissions, server error) and still throws —
      // 2026-07-26: a person's X in the Phase-3 Team grid 404'd here and the
      // aborted refresh left their empty row behind until F5.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) throw err;
    }
  }
  return deleted;
}

// ---------------------------------------------------------------------------
// Pre-removal strand check (skill-gate hardening #4)
// ---------------------------------------------------------------------------

/** A junior left unsupervised if the removed user was the last mentor. */
export interface RemovalStrandUser {
  userKey: string;
  displayName: string;
  level: number | null;
}

/** Response from GET /api/schedule/employee-assignments/removal-check. */
export interface RemovalStrandResult {
  /** True iff removing this user leaves ≥1 junior with no L3+ mentor on the task. */
  strands: boolean;
  taskTypeKey: string | null;
  removedUserLevel: number | null;
  strandedJuniors: RemovalStrandUser[];
}

/**
 * Read-only: would removing `userKey` from this task strand its juniors (no L3+
 * mentor left)? Powers a warn-and-confirm before the chip-× removal.
 *
 * `scenarioKey` scopes the strand set to the active draft's overlay so it matches
 * the scenario-scoped chips; omit it for the non-scenario task table.
 *
 * W2-8 (P-15): `opts.weekStart` + `opts.excludeAssignmentKey` scope the check to
 * a single-week single-row removal (the Phase-2 weekly-cell ×) — only that
 * week's assignees count, and a user still present that week (another row)
 * never strands.
 *
 * Maps to: GET /api/schedule/employee-assignments/removal-check
 */
export async function checkRemovalStranding(
  scheduleTaskKey: string,
  userKey: string,
  scenarioKey?: string,
  opts?: { weekStart?: string; excludeAssignmentKey?: string },
): Promise<RemovalStrandResult> {
  const r = await apiClient.get<RemovalStrandResult>(
    '/schedule/employee-assignments/removal-check',
    {
      params: {
        taskKey: scheduleTaskKey,
        userKey,
        ...(scenarioKey ? { scenarioKey } : {}),
        ...(opts?.weekStart ? { weekStart: opts.weekStart } : {}),
        ...(opts?.excludeAssignmentKey ? { excludeAssignmentKey: opts.excludeAssignmentKey } : {}),
      },
    },
  );
  return r.data;
}

/**
 * POST /api/schedule/tasks/:scheduleTaskKey/place-machine
 * Creates the FIRST MachineBooking for a task that has none. Idempotent.
 */
export async function placeTaskOnMachine(
  scheduleTaskKey: string,
  machineKey: string,
  scenarioKey?: string,
): Promise<{ booking: BookingView; idempotent: boolean }> {
  try {
    const response = await apiClient.post<{ booking: BookingView; idempotent: boolean }>(
      `/schedule/tasks/${scheduleTaskKey}/place-machine`,
      // FEAT-2026-0007 fix: send the active scenarioKey so the first booking lands
      // in the draft scenario (not BASELINE) and is visible in the draft overlay.
      scenarioKey ? { machineKey, scenarioKey } : { machineKey },
    );
    return response.data;
  } catch (err: any) {
    const msg = err?.response?.data?.error
      ?? `Failed to place task on machine: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

/**
 * POST /api/schedule/tasks — create a single manual task.
 * FEAT-2026-0007: backs the Phase-1 "+ Add Task" button (adds one user-picked task).
 */
export async function createScheduleTask(input: {
  name: string;
  taskTypeKey: string;
  source: 'Quote' | 'WorkOrder';
  sourceKey: string;
  presetDurationDays?: number;
  machineCount?: number;
}): Promise<{ ScheduleTaskKey: string }> {
  const response = await apiClient.post<{ ScheduleTaskKey: string }>(
    '/schedule/tasks',
    input,
  );
  return response.data;
}

/**
 * GET /api/schedule/task-types/:key/capable-machines
 */
export async function listCapableMachines(taskTypeKey: string): Promise<CapableMachine[]> {
  try {
    const response = await apiClient.get<CapableMachine[]>(
      `/schedule/task-types/${taskTypeKey}/capable-machines`,
    );
    return response.data;
  } catch (err: any) {
    const msg = err?.response?.data?.error
      ?? `Failed to load capable machines: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}

// ---------------------------------------------------------------------------
// Task search (follow-up 1.7)
// ---------------------------------------------------------------------------

/**
 * A single row returned by GET /api/schedule/tasks/search.
 * Contains enough info for the autocomplete display: key, name, type, source
 * identifier (QuoteNumber or WorkOrderId), and date range.
 */
export interface TaskSearchResult {
  ScheduleTaskKey: string;
  Name: string;
  TaskTypeName: string | null;
  SourceType: 'Quote' | 'WorkOrder' | 'Manual';
  /** QuoteNumber if SourceType = 'Quote' */
  QuoteNumber: string | null;
  /** WorkOrderId if SourceType = 'WorkOrder' */
  WorkOrderId: string | null;
  /** Earliest booking start date (YYYY-MM-DD) or null when unscheduled */
  StartDate: string | null;
  /** Latest booking end date (YYYY-MM-DD) or null when unscheduled */
  EndDate: string | null;
}

export interface SearchScheduleTasksParams {
  q: string;
  /** ISO YYYY-MM-DD — tasks near this date sort first */
  aroundDate?: string;
}

/**
 * GET /api/schedule/tasks/search?q=...&aroundDate=YYYY-MM-DD
 * Returns up to 50 active ScheduleTask rows matching the name substring.
 * Tasks whose bookings overlap [aroundDate-14d, aroundDate+14d] sort first.
 */
export async function searchScheduleTasks(
  params: SearchScheduleTasksParams,
): Promise<TaskSearchResult[]> {
  try {
    const qs = new URLSearchParams({ q: params.q });
    if (params.aroundDate) qs.set('aroundDate', params.aroundDate);
    const response = await apiClient.get<TaskSearchResult[]>(
      `/schedule/tasks/search?${qs.toString()}`,
    );
    return response.data;
  } catch (err: any) {
    const msg = err?.response?.data?.error
      ?? `Failed to search tasks: ${err?.message ?? 'request failed'}`;
    throw new Error(msg);
  }
}
